import { IssueMessageType } from '../../graphql';
import { useCreateIssueMessage } from './useCreateIssueMessage';
import { SideChatSummary } from './useSideChatSummaries';

export type StartSideChatArgs = {
  /** The issue the origin message belongs to (needs `id` + `workspace.id`). */
  issue: { id: string; workspace: { id: string } };
  /** The message the side chat hangs off of. */
  originMessageId: string;
  /**
   * Display name of the origin message's author, used in the divider text.
   * Passed in by the caller (which already has the message's author) rather than
   * resolved here — avoids a new data dependency in the data layer.
   */
  authorName: string;
  /**
   * Current per-origin summaries (from `useSideChatSummaries`). Used to no-op
   * when a side chat already exists for this origin message.
   */
  summaries: Map<string, SideChatSummary>;
};

/**
 * Side Chat (plan 0022 §B): ensure the parented SYSTEM_MESSAGE divider exists
 * for an origin message. Creates it once — only if no side chat exists yet for
 * that message (checked against `summaries`). Idempotent from the caller's POV.
 */
export const useStartSideChat = () => {
  const { createIssueMessage, isPending } = useCreateIssueMessage();

  const startSideChat = async (args: StartSideChatArgs): Promise<void> => {
    // A side chat already exists for this origin — nothing to create.
    if (args.summaries.get(args.originMessageId)?.exists) return;

    await createIssueMessage({
      issue: args.issue,
      message: {
        message: `Side chat started from ${args.authorName}'s message`,
        parentMessage: { id: args.originMessageId },
        type: IssueMessageType.SystemMessage,
      },
    });
  };

  return { startSideChat, isPending };
};
